
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "iconbutton"
    }}>{`IconButton`}</h1>
    <p>{`IconButtons are used to visually distinguish an Icon as an element that the user can interact with to effect a change.
For example, a close button on a Dialog.`}</p>
    <p>{`The IconButton is complete with all requisite DLS base, accessibility and interaction styling and should not be visually altered or extended.`}</p>
    <h3 {...{
      "id": "basic-example"
    }}>{`Basic Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<IconButton aria-label="Close overlay" iconName="navigation-close" />
`}</code></pre>
    <h3 {...{
      "id": "size"
    }}>{`Size`}</h3>
    <p>{`IconButtons render visually at `}<inlineCode parentName="p">{`40px x 40px`}</inlineCode>{` but with a `}<inlineCode parentName="p">{`44px x 44px`}</inlineCode>{` touch target area for WCAG21 compliance. The icon itself is `}<inlineCode parentName="p">{`24px x 24px`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "variant"
    }}>{`Variant`}</h3>
    <p>{`There are two IconButton variants. The default variant is `}<inlineCode parentName="p">{`filled`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <IconButton
    aria-label="E-mail"
    iconName="communication-email"
    variant="filled"
  />
  <IconButton
    aria-label="E-mail"
    iconName="communication-email"
    variant="outline"
  />
</View>
`}</code></pre>
    <h3 {...{
      "id": "intent"
    }}>{`Intent`}</h3>
    <p>{`Intent defines what kind of action the icon button is used for, it's appearance will change based on the intent. The default intent is `}<inlineCode parentName="p">{`primary`}</inlineCode>{`. The `}<inlineCode parentName="p">{`secondary`}</inlineCode>{` intent is intended for rendering IconButtons on a `}<inlineCode parentName="p">{`secondary`}</inlineCode>{` background.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<View spacing={2}>
  <IconButton aria-label="Close" iconName="navigation-close" intent="primary" />
  <IconButton
    aria-label="Close"
    iconName="navigation-close"
    intent="secondary"
  />
  <IconButton
    aria-label="Close"
    iconName="navigation-close"
    intent="destructive"
  />
</View>
`}</code></pre>
    <h3 {...{
      "id": "disabled"
    }}>{`Disabled`}</h3>
    <p>{`Icon Buttons that are disabled can't be interacted with. The visual style of the button also changes to indicate to the user that the buttons are in a disabled state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<IconButton aria-label="Close" iconName="navigation-close" disabled />
`}</code></pre>
    <h3 {...{
      "id": "props---iconbutton"
    }}>{`Props - IconButton`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default Value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`aria-label`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Describe the IconButton's response to interaction @remarks Required, but typed optional as translated strings return undefined on first render`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`autoFocus`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`true to programmatically focus on the IconButton on render`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`disabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`true when IconButton should not be interactable`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`iconName`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The Icon to be displayed within the IconButton`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"alarm" ｜ "blocked" ｜ "calendar" ｜ "checkmark" ｜ "delete" ｜ "designyourown" ｜ "device" ｜ "extendedperiod" ｜ "externallink" ｜ "filter" ｜ "info" ｜ "leaf" ｜ "link" ｜ "map-map" ｜ ... 29 more ... ｜ "thirdparty-youtube"`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`intent`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"primary" ｜ "destructive" ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`primary`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`onClick`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Function to be invoked on IconButton interaction`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`(event: MouseEvent<HTMLButtonElement, MouseEvent>) => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`n/a`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`variant`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`"default" ｜ "outline" ｜ "media" ｜ undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`default`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;